import { useCallback } from "react";

import { toast } from "@/components/ui/sonner";

import { useEdgeFunction } from "./useEdgeFunction";
import { useUserRole } from "./useUserRole";

import type { EdgeFunctionOptions } from "@/lib/edgeFunctionHelpers";

/**
 * Custom hook for making admin-only edge function calls.
 * 
 * Features:
 * - All features from useAuthenticatedEdgeFunction (auth, retry, etc.)
 * - Client-side role check for immediate UX feedback
 * - Server-side admin validation happens in edge function
 * 
 * @example
 * ```tsx
 * const { invokeAdmin, isLoading, isAdmin } = useAdminEdgeFunction();
 * 
 * const handleApprove = async (userId: string) => {
 *   const { data, error } = await invokeAdmin("approve-user", {
 *     body: { userId },
 *     showErrorToast: true,
 *   });
 *   
 *   if (error) return;
 *   // Handle success
 * };
 * 
 * // Disable UI for non-admins
 * if (!isAdmin) return null;
 * ```
 */
export const useAdminEdgeFunction = () => {
  const { execute, isLoading } = useEdgeFunction({ auth: true });
  const { role, loading: roleLoading } = useUserRole();

  const invokeAdmin = useCallback(
    async <T = any>(
      functionName: string,
      options?: EdgeFunctionOptions & { showErrorToast?: boolean }
    ) => {
      // Client-side role check for UX (server validates for security)
      if (!roleLoading && role !== "admin") {
        const error = new Error("Admin access required");
        if (options?.showErrorToast) {
          toast.error("You must be an admin to perform this action");
        }
        return { data: null, error };
      }

      // Call authenticated edge function (server will verify admin role)
      return execute(functionName, options) as Promise<{ data: T | null; error: Error | null }>;
    },
    [execute, role, roleLoading]
  );

  return { 
    invokeAdmin, 
    isLoading: isLoading || roleLoading, 
    isAdmin: role === "admin" 
  };
};
